import {type RefObject, useEffect, useEffectEvent} from 'react'

import {useBoardPaging} from '../provider/contexts'
import type {BoardCell, CellAddress} from '../types'

/**
 * Reports `onApproachItems` for this cell. Only a scroll reports, since reporting on mount would
 * fetch a second page for every cell on screen, and not one to the top, where a new board resets
 * the cell. A drag's auto-scroll would ask for page after page, so a drag reports nothing.
 */
export function useBoardItemApproach({
  address,
  cell,
  bodyRef,
  isDragging,
}: {
  address: CellAddress
  cell: BoardCell<unknown> | undefined
  bodyRef: RefObject<HTMLElement | null>
  isDragging: boolean
}): void {
  const {onApproachItems} = useBoardPaging()
  const {columnKey, rowKey} = address
  // So a caller's inline callback does not re-arm the listener on every render.
  const report = useEffectEvent(() => onApproachItems?.({columnKey, rowKey}))
  const canApproach =
    onApproachItems !== undefined &&
    cell?.hasMoreItems === true &&
    cell.itemsLoaded !== false &&
    cell.isLoadingItems !== true &&
    !isDragging

  // Keyed on the cell, since this package holds no cursor. A page of cards the cell already holds can
  // leave it identical and re-arm nothing, so the caller goes on from there.
  useEffect(() => {
    const body = bodyRef.current
    if (!canApproach || body === null) return

    let frame = 0
    const check = () => {
      frame = 0
      const {scrollTop, scrollHeight, clientHeight} = body
      if (scrollTop <= 0 || scrollHeight - scrollTop - clientHeight > clientHeight) return

      body.removeEventListener('scroll', onScroll)
      report()
    }
    // One layout read per frame, however many scroll events the frame delivers.
    const onScroll = () => {
      if (frame === 0) frame = requestAnimationFrame(check)
    }

    // The question is how far this container has scrolled, which no observer answers without a
    // second element inside the cell's own list of cards. `useScrollEdges` listens the same way.
    // eslint-disable-next-line github/prefer-observers
    body.addEventListener('scroll', onScroll)

    return () => {
      body.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(frame)
    }
  }, [bodyRef, canApproach, cell])
}
